import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { forgotPasswordPath } from '@app/constants/url';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';

export interface CredentialsFormProps {
  email: string;
  password: string;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  submitLabel: string;
  isPending?: boolean;
  error?: string | null;
  emailId: string;
  passwordId: string;
  // Shows the "Forgot password?" link under the password field.
  showForgotPassword?: boolean;
}

/**
 * Presentational email/password form shared by the Employee (real) and Client (mock) login modes,
 * so both look identical. It holds no auth logic — the parent owns state and `onSubmit`.
 */
export const CredentialsForm = ({
  email,
  password,
  setEmail,
  setPassword,
  onSubmit,
  submitLabel,
  isPending = false,
  error,
  emailId,
  passwordId,
  showForgotPassword = false,
}: CredentialsFormProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={emailId} className="sr-only">
          {t('login.email')}
        </Label>
        <Input
          id={emailId}
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={t('login.email')}
          autoComplete="email"
          className="bg-popover"
          required
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={passwordId} className="sr-only">
          {t('login.password')}
        </Label>
        <Input
          id={passwordId}
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder={t('login.password')}
          autoComplete="current-password"
          className="bg-popover"
          required
        />
      </div>
      {showForgotPassword && (
        <div className="-mt-1 flex justify-end">
          <Link
            to={forgotPasswordPath}
            state={{ enterFrom: 'right' }}
            className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            {t('login.forgotPassword')}
          </Link>
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" loading={isPending} className="w-full">
        {submitLabel}
      </Button>
    </form>
  );
};
